import { Edit } from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import api from '../../api';
import MoneyInputWithBlur from '../MoneyInputWithBlur';
import ExternalOrderIdDisplay from './ExternalOrderIdDisplay';
import OrderStagesPanel from './OrderStagesPanel';
import { formatCurrencyInCents, formatDate, formatDocument, presentPhone, presentUuid } from '../../helpers/formatters';
import { applyEmail, applyName } from '../../helpers/captureMode';
import {
    calculateBalance,
    calculateCommission,
    calculateNetReceived,
    calculateProductCosts,
    calculateProfitMargin,
    calculateTotalPaid,
} from './orderDetailsHelpers';

function SectionTitle({ children }) {
    return (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {children}
        </Typography>
    );
}

export default function OrderDetailsBody({ order, transaction, onOrderUpdated }) {
    const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
    const [commissionDraft, setCommissionDraft] = useState(0);
    const [savingCommission, setSavingCommission] = useState(false);
    const [commissionError, setCommissionError] = useState('');

    if (!order) {
        return null;
    }

    const commissionCents = Number(order.commission) || 0;
    const appliedCommission = calculateCommission(order);

    const openCommissionDialog = () => {
        setCommissionDraft((Number(order.commission) || 0) / 100);
        setCommissionError('');
        setCommissionDialogOpen(true);
    };

    const saveCommission = async () => {
        const cents = Math.max(0, Math.round((Number(commissionDraft) || 0) * 100));
        setSavingCommission(true);
        setCommissionError('');
        try {
            const response = await api.put(`orders/${order.id}/commission`, { commission: cents });
            if (response.data?.success && response.data?.data) {
                if (typeof onOrderUpdated === 'function') {
                    onOrderUpdated(response.data.data);
                }
                setCommissionDialogOpen(false);
            } else {
                setCommissionError('Não foi possível salvar a comissão.');
            }
        } catch (error) {
            setCommissionError(error?.response?.data?.message || 'Não foi possível salvar a comissão.');
        } finally {
            setSavingCommission(false);
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <OrderStagesPanel order={order} onOrderUpdated={onOrderUpdated} />
                </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
                <SectionTitle>Informações do Cliente</SectionTitle>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography variant="body2"><strong>Nome:</strong> {applyName(order.customer?.name) || 'N/A'}</Typography>
                    {order.customer?.email && (
                        <Typography variant="body2"><strong>Email:</strong> {applyEmail(order.customer.email)}</Typography>
                    )}
                    {order.customer?.phone && (
                        <Typography variant="body2"><strong>Telefone:</strong> {presentPhone(order.customer.phone)}</Typography>
                    )}
                    {order.customer?.document && (
                        <Typography variant="body2"><strong>Documento:</strong> {formatDocument(order.customer.document)}</Typography>
                    )}
                </Box>
            </Grid>

            <Grid item xs={12} md={6}>
                <SectionTitle>Informações da Venda</SectionTitle>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography variant="body2"><strong>ID:</strong> {order.id ? presentUuid(order.id) : 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Data:</strong> {formatDate(order.created_at)}</Typography>
                    <Typography variant="body2"><strong>Canal de Venda:</strong> {order.sales_channel?.name || 'N/A'}</Typography>
                    <Typography variant="body2" component="div">
                        <strong>Status do Pagamento:</strong>
                        <Chip
                            label={order.payment_details?.paid ? 'Pago' : 'Pendente'}
                            color={order.payment_details?.paid ? 'success' : 'warning'}
                            size="small"
                            sx={{ ml: 1 }}
                        />
                    </Typography>
                    <Typography variant="body2" component="div">
                        <strong>Conciliação bancária:</strong>
                        <Chip
                            label={
                                order.bank_reconciled
                                    ? 'Bancária'
                                    : order.payment_received
                                        ? 'Realizada'
                                        : 'Pendente'
                            }
                            color={order.bank_reconciled || order.payment_received ? 'success' : 'warning'}
                            size="small"
                            sx={{ ml: 1 }}
                        />
                    </Typography>
                    {order.payment_methods && order.payment_methods.length > 0 && (
                        <Typography variant="body2">
                            <strong>Pagamento:</strong>
                            {' '}
                            {order.payment_methods.map((method, index) => {
                                const parts = [method.name];
                                if (method.installments > 1) {
                                    parts.push(`${method.installments}x`);
                                }
                                if (method.amount) {
                                    parts.push(formatCurrencyInCents(method.amount));
                                }
                                return (
                                    <span key={`${method.name}-${index}`}>
                                        {index > 0 ? ' + ' : ''}
                                        {parts.join(' ')}
                                    </span>
                                );
                            })}
                        </Typography>
                    )}
                    {(!order.payment_methods || order.payment_methods.length === 0) && order.payment_details?.payment_method && (
                        <Typography variant="body2">
                            <strong>Método de Pagamento:</strong> {order.payment_details.payment_method}
                        </Typography>
                    )}
                    {order.observations && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            <strong>Observações:</strong> {order.observations}
                        </Typography>
                    )}
                </Box>
            </Grid>

            <Grid item xs={12}>
                <SectionTitle>Produtos</SectionTitle>
                <Divider sx={{ mb: 1.5 }} />
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Produto</strong></TableCell>
                                <TableCell><strong>Quantidade</strong></TableCell>
                                <TableCell><strong>Preço Unitário</strong></TableCell>
                                <TableCell><strong>Subtotal</strong></TableCell>
                                <TableCell><strong>Custo de produção</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(order.products || []).map((product, index) => (
                                <TableRow key={index}>
                                    <TableCell>{product?.name || 'N/A'}</TableCell>
                                    <TableCell>{product?.quantity || 0}</TableCell>
                                    <TableCell>
                                        {product?.base_price ? formatCurrencyInCents(product.base_price) : 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                        {product?.base_price && product?.quantity
                                            ? formatCurrencyInCents(product.base_price * product.quantity)
                                            : 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                        {product?.cost_price && product?.quantity
                                            ? formatCurrencyInCents(product.cost_price * product.quantity)
                                            : product?.cost_price
                                                ? formatCurrencyInCents(product.cost_price)
                                                : 'N/A'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            <Grid item xs={12} md={6}>
                <SectionTitle>Frete</SectionTitle>
                <Divider sx={{ mb: 1.5 }} />
                {order.shipping_method ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Typography variant="body2"><strong>Tipo:</strong> {order.shipping_method.name}</Typography>
                        {order.shipping_method.description && (
                            <Typography variant="body2"><strong>Descrição:</strong> {order.shipping_method.description}</Typography>
                        )}
                        <Typography variant="body2">
                            <strong>Prazo de Entrega:</strong> {order.shipping_method.delivery_days} dias
                        </Typography>
                        <Typography variant="body2">
                            <strong>Custo:</strong>{' '}
                            {order.shipping_method.cost ? formatCurrencyInCents(order.shipping_method.cost) : 'Grátis'}
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="body2">Retirada no local</Typography>
                )}
            </Grid>

            <Grid item xs={12} md={6}>
                <SectionTitle>Pagamento</SectionTitle>
                <Divider sx={{ mb: 1.5 }} />
                {order.payment_methods && order.payment_methods.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {order.payment_methods.map((method, index) => (
                            <Box key={`${method.name}-${index}`}>
                                <Typography variant="body2">
                                    <strong>Pagamento {index + 1}:</strong> {method.name}
                                    {method.installments > 1 ? ` (${method.installments}x)` : ''}
                                    {' — '}
                                    {formatCurrencyInCents(method.amount)}
                                </Typography>
                                {method.payment_date && (
                                    <Typography variant="caption" color="text.secondary">
                                        Data: {formatDate(method.payment_date)}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2">Informações de pagamento não disponíveis</Typography>
                )}
            </Grid>

            <Grid item xs={12}>
                <SectionTitle>Resumo Financeiro</SectionTitle>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Subtotal:</Typography>
                        <Typography variant="body2">
                            {order.amount ? formatCurrencyInCents(order.amount) : 'N/A'}
                        </Typography>
                    </Box>
                    {order.shipping_method && order.shipping_method.cost > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Frete:</Typography>
                            <Typography variant="body2">{formatCurrencyInCents(order.shipping_method.cost)}</Typography>
                        </Box>
                    )}
                    {order.discount && order.discount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">
                                Desconto{order.discount_reason ? ` (${order.discount_reason})` : ''}:
                            </Typography>
                            <Typography variant="body2">-{formatCurrencyInCents(order.discount)}</Typography>
                        </Box>
                    )}
                    {order.external_order_id ? (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="body2">ID no canal:</Typography>
                            <ExternalOrderIdDisplay
                                externalOrderId={order.external_order_id}
                                externalOrderUrl={order.external_order_url}
                                salesChannelName={order.sales_channel?.name}
                                prefix=""
                                color="text.primary"
                            />
                        </Box>
                    ) : null}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2"><strong>Valor Pago:</strong></Typography>
                        <Typography variant="body2"><strong>{formatCurrencyInCents(calculateTotalPaid(order))}</strong></Typography>
                    </Box>
                    {order.expected_net != null && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Líquido esperado:</Typography>
                            <Typography variant="body2">{formatCurrencyInCents(order.expected_net)}</Typography>
                        </Box>
                    )}
                    <Divider sx={{ my: 0.5 }} />
                    {transaction && transaction.deduction > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Deduções/Taxas:</Typography>
                            <Typography variant="body2" color="error.main">
                                -{formatCurrencyInCents(transaction.deduction)}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Valor Recebido (líquido):</Typography>
                        <Typography variant="body2" color={transaction ? 'success.main' : 'warning.main'}>
                            {transaction ? formatCurrencyInCents(calculateNetReceived(transaction)) : 'Sem transação'}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Custo dos Produtos:</Typography>
                        <Typography variant="body2" color="error.main">
                            -{formatCurrencyInCents(calculateProductCosts(order))}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">Comissão:</Typography>
                            <IconButton
                                size="small"
                                aria-label="Informar comissão"
                                onClick={openCommissionDialog}
                                sx={{ p: 0.25 }}
                            >
                                <Edit fontSize="inherit" />
                            </IconButton>
                        </Box>
                        <Typography
                            variant="body2"
                            color={appliedCommission > 0 ? 'error.main' : 'text.primary'}
                        >
                            {appliedCommission > 0
                                ? `-${formatCurrencyInCents(appliedCommission)}`
                                : formatCurrencyInCents(commissionCents)}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1">Saldo:</Typography>
                        <Typography
                            variant="subtitle1"
                            color={calculateBalance(order, transaction) >= 0 ? 'success.main' : 'error.main'}
                        >
                            {formatCurrencyInCents(calculateBalance(order, transaction))}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Margem de Lucro:</Typography>
                        <Typography
                            variant="body2"
                            color={calculateProfitMargin(order, transaction) >= 0 ? 'success.main' : 'error.main'}
                        >
                            {calculateProfitMargin(order, transaction).toFixed(1)}%
                        </Typography>
                    </Box>
                </Box>
            </Grid>

            <Dialog open={commissionDialogOpen} onClose={() => !savingCommission && setCommissionDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Informar comissão</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <MoneyInputWithBlur
                            label="Comissão"
                            value={commissionDraft}
                            onChange={(values) => setCommissionDraft(values.value === '' ? 0 : Number(values.value) || 0)}
                        />
                        {commissionError ? (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                {commissionError}
                            </Typography>
                        ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Informe 0 para remover a comissão do cálculo do saldo.
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCommissionDialogOpen(false)} disabled={savingCommission}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={saveCommission}
                        disabled={savingCommission}
                        startIcon={savingCommission ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}
